/* Bounded, viewport-driven cache shared by the discovery sequences. */
(() => {
  'use strict';
  if (window.SLFrameCache) return;

  // Mobile keeps compressed views for the visit and recycles only decoded pixels.
  // Finishing an in-flight view avoids starvation during a quick scroll/reversal.
  function mobileCache(urls, options) {
    const blobs=new Map(),ready=new Map(),fetching=new Map(),decoding=new Set(),failed=new Set(),listeners=new Set();
    const count=urls.length;let focus=0,direction=1,paused=true,disposed=false,started=false,timer=0,requests=0,aborts=0;
    const norm=i=>options.wrap?((i%count)+count)%count:Math.max(0,Math.min(count-1,i));
    function nearby(limit){const list=[focus];for(let n=1;list.length<Math.min(limit,count)&&n<count;n++)for(const j of[focus+n*direction,focus-n*direction]){const i=norm(j);if(!list.includes(i)&&list.length<limit)list.push(i);}return list;}
    const emit=()=>{for(const fn of listeners)fn();};
    const release=f=>{f.image.close?.();if(f.objectUrl){f.image.src='';URL.revokeObjectURL(f.objectUrl);}};
    function touch(i){const f=ready.get(i);if(f){ready.delete(i);ready.set(i,f);}}
    function trim(){const wanted=new Set(nearby(options.decodeAhead));for(const[i,f]of ready){if(ready.size<=options.decodeLimit)break;if(!wanted.has(i)){release(f);ready.delete(i);}}}
    function schedule(){if(!timer&&!paused&&!disposed)timer=setTimeout(()=>{timer=0;pump();},0);}
    async function decode(blob){if(typeof createImageBitmap==='function'){try{return{image:await createImageBitmap(blob)}}catch{}}
      const objectUrl=URL.createObjectURL(blob),image=new Image();image.decoding='async';image.src=objectUrl;
      try{await image.decode();return{image,objectUrl}}catch(error){URL.revokeObjectURL(objectUrl);throw error;}}
    function pump(){if(paused||disposed)return;
      const priority=nearby(options.prefetch);
      // Once nearby views are covered, fill the small compressed sequence progressively.
      const order=[...priority,...Array.from({length:count},(_,i)=>i).filter(i=>!priority.includes(i))];
      for(const i of order){if(fetching.size>=options.concurrency)break;if(blobs.has(i)||fetching.has(i)||failed.has(i))continue;
        const controller=new AbortController();fetching.set(i,controller);requests++;
        fetch(urls[i],{signal:controller.signal,headers:{Accept:'image/webp,image/*;q=0.8'}})
          .then(r=>{if(!r.ok)throw Error('Frame unavailable');return r.blob()})
          .then(blob=>{if(!disposed)blobs.set(i,blob)})
          .catch(error=>{if(error.name==='AbortError')aborts++;else{failed.add(i);emit();}})
          .finally(()=>{fetching.delete(i);schedule();});
      }
      for(const i of nearby(options.decodeAhead)){if(decoding.size>=2)break;if(!blobs.has(i)||ready.has(i)||decoding.has(i)||failed.has(i))continue;
        decoding.add(i);decode(blobs.get(i)).then(frame=>{
          if(disposed||paused){release(frame);return;}
          ready.set(i,{...frame,index:i});trim();emit();
        }).catch(()=>{failed.add(i);emit();}).finally(()=>{decoding.delete(i);schedule();});
      }
    }
    function pause(){paused=true;clearTimeout(timer);timer=0;for(const c of fetching.values())c.abort();}
    return{start(){if(disposed)return;started=true;paused=false;schedule();},prepare(i){i=norm(Math.round(i));if(i===focus)return;const delta=options.wrap?(i-focus+count)%count:i-focus;direction=options.wrap?(delta<=count/2?1:-1):(delta>0?1:-1);focus=i;schedule();},pause,touch,
      destroy(){disposed=true;pause();for(const f of ready.values())release(f);ready.clear();blobs.clear();listeners.clear();},subscribe(fn){listeners.add(fn);return()=>listeners.delete(fn)},retry(){failed.clear();this.start();},get ready(){return ready},get failed(){return nearby(options.decodeAhead).some(i=>failed.has(i))},
      get stats(){return{started,paused,focus,available:blobs.size,decoded:ready.size,fetching:fetching.size,limit:options.decodeLimit,requests,aborts,compressedBytes:[...blobs.values()].reduce((n,b)=>n+b.size,0),decodedBytes:[...ready.values()].reduce((n,f)=>n+(f.image.width||f.image.naturalWidth)*(f.image.height||f.image.naturalHeight)*4,0)}}};
  }

  window.SLFrameCache = (urls, options) => {
    if (options.mobileStable) return mobileCache(urls, options);
    const count = urls.length;
    const blobs = new Map(), ready = new Map(), fetching = new Map(), decoding = new Set();
    const failed = new Set(), listeners = new Set();
    let focus = 0, direction = 1, paused = true, disposed = false, timer = 0, started = false;
    const normalize = i => options.wrap ? ((i % count) + count) % count : Math.max(0, Math.min(count - 1, i));
    function windowFor(limit) {
      const list = [focus];
      if (options.anchor && focus !== 0) list.push(0);
      for (let n = 1; list.length < Math.min(limit, count) && n < count; n++) {
        for (const item of [focus + n * direction, focus - n * direction]) {
          const i = normalize(item);
          if (!list.includes(i) && list.length < limit) list.push(i);
        }
      }
      return list;
    }
    function release(frame) {
      frame.image.close?.();
      if (frame.objectUrl) { frame.image.src = ''; URL.revokeObjectURL(frame.objectUrl); }
    }
    function trim() {
      const keep = new Set(windowFor(options.decodeLimit));
      for (const [i, frame] of ready) if (!keep.has(i)) { release(frame); ready.delete(i); }
      const wanted = new Set(windowFor(options.prefetch));
      for (const [i, controller] of fetching) if (!wanted.has(i)) controller.abort();
      for (const i of blobs.keys()) {
        if (blobs.size <= options.blobLimit) break;
        if (!wanted.has(i) && !decoding.has(i)) blobs.delete(i);
      }
    }
    const notify = () => { for (const fn of listeners) fn(); };
    function schedule() {
      if (!timer && !paused && !disposed) timer = setTimeout(() => { timer = 0; pump(); }, 0);
    }
    async function decode(blob) {
      if (typeof createImageBitmap === 'function') {
        try { return { image: await createImageBitmap(blob) }; } catch { /* Safari image fallback. */ }
      }
      const objectUrl = URL.createObjectURL(blob), image = new Image();
      image.decoding = 'async'; image.src = objectUrl;
      try { await image.decode(); return { image, objectUrl }; }
      catch (error) { URL.revokeObjectURL(objectUrl); throw error; }
    }
    function pump() {
      if (paused || disposed) return;
      trim();
      for (const i of windowFor(options.prefetch)) {
        if (fetching.size >= options.concurrency) break;
        if (blobs.has(i) || fetching.has(i) || failed.has(i)) continue;
        const controller = new AbortController(); fetching.set(i, controller);
        fetch(urls[i], { signal: controller.signal, headers: { Accept: 'image/webp,image/*;q=0.8' } })
          .then(response => { if (!response.ok) throw Error('Frame unavailable'); return response.blob(); })
          .then(blob => { if (!disposed) { blobs.delete(i); blobs.set(i, blob); trim(); } })
          .catch(error => { if (error.name !== 'AbortError') failed.add(i); })
          .finally(() => { fetching.delete(i); if (!disposed) { notify(); schedule(); } });
      }
      for (const i of windowFor(options.decodeLimit)) {
        if (decoding.size >= 2) break;
        if (!blobs.has(i) || ready.has(i) || decoding.has(i) || failed.has(i)) continue;
        decoding.add(i);
        decode(blobs.get(i)).then(frame => {
          if (disposed || paused || !windowFor(options.decodeLimit).includes(i)) { release(frame); return; }
          ready.set(i, { ...frame, index: i }); trim();
        }).catch(() => failed.add(i)).finally(() => { decoding.delete(i); if (!disposed) { notify(); schedule(); } });
      }
    }
    function pause() {
      paused = true; clearTimeout(timer); timer = 0;
      for (const controller of fetching.values()) controller.abort();
    }
    return {
      start() { if (!disposed) { started = true; paused = false; schedule(); } },
      prepare(index) {
        index = normalize(Math.round(index)); if (index === focus) return;
        const delta = options.wrap ? (index - focus + count) % count : index - focus;
        direction = options.wrap ? (delta <= count / 2 ? 1 : -1) : (delta > 0 ? 1 : -1);
        focus = index; trim(); schedule();
      },
      pause,
      destroy() { disposed = true; pause(); for (const frame of ready.values()) release(frame); ready.clear(); blobs.clear(); listeners.clear(); },
      subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
      retry() { failed.clear(); this.start(); },
      get ready() { return ready; },
      get failed() { return windowFor(options.decodeLimit).some(i => failed.has(i)); },
      get stats() { return { started, paused, focus, available: blobs.size, decoded: ready.size, fetching: fetching.size, limit: options.decodeLimit,
        compressedBytes: [...blobs.values()].reduce((n, blob) => n + blob.size, 0),
        decodedBytes: [...ready.values()].reduce((n, f) => n + (f.image.width || f.image.naturalWidth) * (f.image.height || f.image.naturalHeight) * 4, 0) }; }
    };
  };
})();
